import { GameFrame, type GameFrameHandle } from '@mogzo/play';
import type { LaunchParams, Orientation } from '@mogzo/shared';
import { X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useChatCounts } from '../../lib/chat';
import { useMatchChat } from '../../lib/match-chat';
import { useCanChat } from '../chat/ChatDock';
import { ChatView } from '../chat/ChatView';
import { InboxPanel } from '../chat/InboxPanel';
import { ReportDialog } from '../chat/ReportDialog';

/**
 * The player's own messages over the game on a phone (M38b), in MOGZO's look: the inbox, then a
 * chat. Closing it goes back to the game where it was.
 */
function MessagesOverGame({ onClose }: { onClose: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const [tab, setTab] = useState<'chats' | 'requests'>('chats');
  return (
    <div className="absolute inset-0 z-[60] flex flex-col justify-end bg-black/55">
      <section
        aria-label="Your messages"
        className="relative flex h-[calc(100%-72px)] flex-col overflow-hidden rounded-t-[22px] bg-card text-ink"
      >
        <button
          type="button"
          aria-label="Back to the game"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 flex size-10 items-center justify-center rounded-full bg-field"
        >
          <X size={20} strokeWidth={1.9} aria-hidden="true" />
        </button>
        {open ? (
          <ChatView
            key={open}
            id={open}
            variant="dock"
            onBack={() => setOpen(null)}
            onGone={() => setOpen(null)}
          />
        ) : (
          <InboxPanel
            variant="dock"
            tab={tab}
            onTab={setTab}
            onOpen={setOpen}
            onNew={() => undefined}
          />
        )}
      </section>
    </div>
  );
}

/**
 * A launched game on a phone: the whole screen, with MOGZO's pause button as the only control;
 * it opens MOGZO's pause menu (M19). Real full screen where the browser allows it (not on
 * iPhones, where the layer covers the page instead). The phone's Back button closes it.
 */
export function PhonePlayer({
  url,
  name,
  projectId,
  orientation,
  launch,
  isDeveloper,
  onExit,
}: {
  url: string;
  name: string;
  projectId: string;
  orientation: Orientation;
  launch: LaunchParams;
  isDeveloper: boolean;
  onExit: () => void;
}) {
  const layer = useRef<HTMLDivElement>(null);
  const game = useRef<GameFrameHandle>(null);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  // Chat (M38b): the match's chat over the game, and the player's own messages.
  const canChat = useCanChat();
  const counts = useChatCounts(canChat);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const messagesRow = useMemo(
    () => (canChat ? { unread: counts?.unread ?? 0, open: () => setMessagesOpen(true) } : null),
    [canChat, counts?.unread],
  );
  const matchChat = useMatchChat(game, { enabled: canChat, messages: messagesRow });
  const lines = matchChat.view?.messages ?? [];
  const [seen, setSeen] = useState(0);
  const unseen = lines.slice(seen).filter((m) => !m.mine).length + (counts?.unread ?? 0);

  useEffect(() => {
    const element = layer.current;
    // Launched from a tap, so the browser allows full screen; refusals are fine.
    element
      ?.requestFullscreen?.({ navigationUI: 'hide' })
      .then(() => {
        const lock = (screen.orientation as { lock?: (to: string) => Promise<void> }).lock;
        if (orientation !== 'any' && lock) {
          return lock.call(screen.orientation, orientation).catch(() => undefined);
        }
      })
      .catch(() => undefined);
    const scroll = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Back closes the game instead of leaving the page.
    history.pushState({ mogzoGame: true }, '');
    const onBack = () => onExitRef.current();
    window.addEventListener('popstate', onBack);
    return () => {
      window.removeEventListener('popstate', onBack);
      document.body.style.overflow = scroll;
      if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    };
  }, [orientation]);

  function exit() {
    // Leaves the history entry this layer added; popstate then closes it.
    if (history.state?.mogzoGame) history.back();
    else onExit();
  }

  return createPortal(
    <div
      ref={layer}
      role="dialog"
      aria-modal="true"
      aria-label={name}
      className="fixed inset-0 z-50 bg-black"
    >
      <GameFrame
        ref={game}
        url={url}
        title={name}
        projectId={projectId}
        launch={launch}
        isDeveloper={isDeveloper}
        panels={{ device: 'phone', gameName: name, besidePauseButton: true, onExit: exit }}
        chat={matchChat.frameChat}
      />
      <button
        type="button"
        onClick={() => {
          setSeen(lines.length);
          game.current?.openPanel({ kind: 'pause' });
        }}
        aria-label={unseen ? `Pause, ${unseen} new messages` : 'Pause'}
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          left: 'calc(env(safe-area-inset-left, 0px) + 12px)',
        }}
        className="absolute flex size-11 items-center justify-center rounded-full bg-black/40 text-white"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </svg>
        {unseen > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 box-border grid h-5 min-w-5 place-items-center rounded-full border-2 border-black bg-brand px-1 text-[11px] font-bold text-ink"
          >
            {unseen > 9 ? '9+' : unseen}
          </span>
        )}
      </button>
      {messagesOpen && <MessagesOverGame onClose={() => setMessagesOpen(false)} />}
      {matchChat.reporting && (
        <ReportDialog message={matchChat.reporting} onClose={() => matchChat.setReporting(null)} />
      )}
    </div>,
    document.body,
  );
}
